import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  TextInput,
  Stack,
  Group,
  Text,
  Image,
  Avatar,
  Loader,
  Center,
  Paper,
  UnstyledButton,
  Box,
  ActionIcon,
  Card,
  Tabs,
  Badge,
  Rating,
  Anchor,
} from '@mantine/core';
import {
  IconSearch,
  IconPackage,
  IconUser,
  IconThumbUp,
  IconMessageCircle,
  IconHash,
  IconCategory,
} from '@tabler/icons-react';
import { useDebouncedValue, useClickOutside } from '@mantine/hooks';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { searchService } from '../../services/search';
import { hashtagsService } from '../../services/hashtags';
import { StarRating } from '../../components/reviews';
import type { User, Product } from '../../types';
import type { ReviewSearchResult } from '../../services/search';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || searchParams.get('category') || '';

  const [query, setQuery] = useState(urlQuery);
  const [debouncedQuery] = useDebouncedValue(query, 400);
  const [submittedQuery, setSubmittedQuery] = useState(urlQuery);
  const [activeTab, setActiveTab] = useState<string | null>(null);

  useEffect(() => {
    setQuery(urlQuery);
    setSubmittedQuery(urlQuery);
  }, [urlQuery]);

  const dropdownRef = useClickOutside(() => setShowDropdown(false));
  const [showDropdown, setShowDropdown] = useState(false);

  // Autocomplete (while typing)
  const { data: suggestionsData, isFetching: isLoadingSuggestions } = useQuery({
    queryKey: ['search-suggestions', debouncedQuery],
    queryFn: () => searchService.search(debouncedQuery),
    enabled: !!debouncedQuery.trim() && debouncedQuery !== submittedQuery,
    staleTime: 30_000,
    select: (data) => ({
      products: data.products.slice(0, 3),
      users: data.users.slice(0, 2),
    }),
  });
  const suggestions = suggestionsData ?? { products: [], users: [] };

  // Full results — infinite per tab type
  const {
    data: productPages, isFetching: isLoadingProducts,
    fetchNextPage: fetchMoreProducts, hasNextPage: hasMoreProducts, isFetchingNextPage: fetchingMoreProducts,
  } = useInfiniteQuery({
    queryKey: ['search-results-products', submittedQuery],
    queryFn: ({ pageParam = 1 }) => searchService.search(submittedQuery, pageParam as number, 20, 'products'),
    getNextPageParam: (last, pages) =>
      pages.reduce((s, p) => s + p.products.length, 0) < last.productTotalCount ? pages.length + 1 : undefined,
    initialPageParam: 1,
    enabled: !!submittedQuery.trim(),
    staleTime: 30_000,
  });

  const {
    data: reviewPages, isFetching: isLoadingReviews,
    fetchNextPage: fetchMoreReviews, hasNextPage: hasMoreReviews, isFetchingNextPage: fetchingMoreReviews,
  } = useInfiniteQuery({
    queryKey: ['search-results-reviews', submittedQuery],
    queryFn: ({ pageParam = 1 }) => searchService.search(submittedQuery, pageParam as number, 20, 'reviews'),
    getNextPageParam: (last, pages) =>
      pages.reduce((s, p) => s + p.reviews.length, 0) < last.reviewTotalCount ? pages.length + 1 : undefined,
    initialPageParam: 1,
    enabled: !!submittedQuery.trim(),
    staleTime: 30_000,
  });

  const {
    data: userPages, isFetching: isLoadingUsers,
    fetchNextPage: fetchMoreUsers, hasNextPage: hasMoreUsers, isFetchingNextPage: fetchingMoreUsers,
  } = useInfiniteQuery({
    queryKey: ['search-results-users', submittedQuery],
    queryFn: ({ pageParam = 1 }) => searchService.search(submittedQuery, pageParam as number, 20, 'users'),
    getNextPageParam: (last, pages) =>
      pages.reduce((s, p) => s + p.users.length, 0) < last.userTotalCount ? pages.length + 1 : undefined,
    initialPageParam: 1,
    enabled: !!submittedQuery.trim(),
    staleTime: 30_000,
  });

  // Hashtag search
  const { data: tagResults = [], isFetching: isLoadingTags } = useQuery({
    queryKey: ['search-tags', submittedQuery],
    queryFn: () => hashtagsService.search(submittedQuery, 20),
    enabled: !!submittedQuery.trim(),
    staleTime: 30_000,
  });

  const isLoadingResults = isLoadingProducts || isLoadingReviews || isLoadingUsers;
  const products = productPages?.pages.flatMap((p) => p.products) ?? [];
  const reviews = reviewPages?.pages.flatMap((p) => p.reviews) ?? [];
  const users = userPages?.pages.flatMap((p) => p.users) ?? [];

  const productSentinel = useInfiniteScroll(fetchMoreProducts, !!hasMoreProducts, fetchingMoreProducts);
  const reviewSentinel = useInfiniteScroll(fetchMoreReviews, !!hasMoreReviews, fetchingMoreReviews);
  const userSentinel = useInfiniteScroll(fetchMoreUsers, !!hasMoreUsers, fetchingMoreUsers);
  const productTotal = productPages?.pages[0]?.productTotalCount ?? 0;
  const reviewTotal = reviewPages?.pages[0]?.reviewTotalCount ?? 0;
  const userTotal = userPages?.pages[0]?.userTotalCount ?? 0;

  // Auto-select the tab with the most results once data loads
  useEffect(() => {
    if (!isLoadingResults && submittedQuery && activeTab === null) {
      const counts = [
        { tab: 'products', count: productTotal },
        { tab: 'reviews', count: reviewTotal },
        { tab: 'users', count: userTotal },
        { tab: 'tags', count: tagResults.length },
      ];
      const best = counts.reduce((a, b) => (b.count > a.count ? b : a));
      setActiveTab(best.count > 0 ? best.tab : 'products');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingResults, submittedQuery]);

  // Reset tab selection when query changes
  useEffect(() => { setActiveTab(null); }, [submittedQuery]);

  const showDropdownDerived =
    showDropdown &&
    !!debouncedQuery.trim() &&
    debouncedQuery !== submittedQuery &&
    (isLoadingSuggestions || suggestions.products.length > 0 || suggestions.users.length > 0);

  const handleSearch = (searchQuery?: string) => {
    const q = (searchQuery ?? query).trim();
    if (!q) return;
    setSubmittedQuery(q);
    setShowDropdown(false);
    setSearchParams({ q });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
    else if (e.key === 'Escape') setShowDropdown(false);
  };

  const hasSuggestions = suggestions.products.length > 0 || suggestions.users.length > 0;

  return (
    <Container size="md" pt="md" pb="xl">
      <Stack gap="lg">
        {/* Search input */}
        <Box style={{ position: 'relative' }} ref={dropdownRef}>
          <TextInput
            size="lg"
            placeholder="Search products, reviews, people, tags..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowDropdown(!!e.target.value.trim());
            }}
            onFocus={() => {
              if (hasSuggestions && query.trim() && query !== submittedQuery)
                setShowDropdown(true);
            }}
            onKeyDown={handleKeyDown}
            leftSection={<IconSearch size={20} />}
            rightSection={
              <ActionIcon variant="subtle" onClick={() => handleSearch()} aria-label="Search">
                <IconSearch size={18} />
              </ActionIcon>
            }
            autoFocus
          />

          {/* Autocomplete dropdown */}
          {showDropdownDerived && (
            <Paper
              shadow="md" radius="md" withBorder
              style={{
                position: 'absolute',
                top: '100%', left: 0, right: 0,
                zIndex: 100, marginTop: 4, overflow: 'hidden',
              }}
            >
              {isLoadingSuggestions && !hasSuggestions ? (
                <Center py="sm"><Loader size="sm" /></Center>
              ) : (
                <Stack gap={0}>
                  {suggestions.products.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="sm" pt="xs" pb={4}>
                        Products
                      </Text>
                      {suggestions.products.map((product) => (
                        <UnstyledButton
                          key={product.id}
                          component={Link}
                          to={`/products/${product.id}`}
                          py={6} px="sm"
                          onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                        >
                          <Group gap="sm" wrap="nowrap">
                            <Center w={32} h={32} style={{
                              borderRadius: 'var(--mantine-radius-sm)',
                              backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))',
                              flexShrink: 0, overflow: 'hidden',
                            }}>
                              {product.imageUrl
                                ? <Image src={product.imageUrl} alt="" w={32} h={32} fit="contain" />
                                : <IconPackage size={14} color="var(--mantine-color-dimmed)" />}
                            </Center>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={500} truncate>{product.name}</Text>
                              {product.brand && <Text size="xs" c="dimmed" truncate>{product.brand}</Text>}
                            </div>
                            {product.averageRating && <StarRating rating={product.averageRating} size="xs" />}
                          </Group>
                        </UnstyledButton>
                      ))}
                    </>
                  )}

                  {suggestions.users.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="sm" pt="xs" pb={4}>
                        People
                      </Text>
                      {suggestions.users.map((user) => (
                        <UnstyledButton
                          key={user.id}
                          component={Link}
                          to={`/users/${user.username}`}
                          py={6} px="sm"
                          onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                        >
                          <Group gap="sm" wrap="nowrap">
                            <Avatar src={user.profileImageUrl} alt="" size="sm" radius="xl">
                              <IconUser size={14} />
                            </Avatar>
                            <Text size="sm" fw={500} truncate>@{user.username}</Text>
                          </Group>
                        </UnstyledButton>
                      ))}
                    </>
                  )}

                  <UnstyledButton
                    py="xs" px="sm"
                    onClick={() => handleSearch()}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                    style={{
                      borderTop: '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <Group gap="xs">
                      <IconSearch size={14} />
                      <Text size="sm" c="blue">Search for &ldquo;{query.trim()}&rdquo;</Text>
                    </Group>
                  </UnstyledButton>
                </Stack>
              )}
            </Paper>
          )}
        </Box>

        {/* Loading */}
        {isLoadingResults && products.length === 0 && reviews.length === 0 && users.length === 0 && (
          <Center py="xl"><Loader size="lg" /></Center>
        )}

        {/* Tabbed results */}
        {submittedQuery && (products.length > 0 || reviews.length > 0 || users.length > 0 || isLoadingResults || isLoadingTags) && (
          <Tabs value={activeTab ?? 'products'} onChange={setActiveTab}>
            <Box style={{ overflowX: 'auto' }}>
              <Tabs.List mb="md" style={{ flexWrap: 'nowrap' }}>
                <Tabs.Tab
                  value="products"
                  leftSection={<IconPackage size={14} />}
                  rightSection={productTotal > 0 ? <Badge size="xs" variant="light">{productTotal}</Badge> : undefined}
                >
                  Products
                </Tabs.Tab>
                <Tabs.Tab
                  value="reviews"
                  leftSection={<IconCategory size={14} />}
                  rightSection={reviewTotal > 0 ? <Badge size="xs" variant="light">{reviewTotal}</Badge> : undefined}
                >
                  Reviews
                </Tabs.Tab>
                <Tabs.Tab
                  value="users"
                  leftSection={<IconUser size={14} />}
                  rightSection={userTotal > 0 ? <Badge size="xs" variant="light">{userTotal}</Badge> : undefined}
                >
                  People
                </Tabs.Tab>
                <Tabs.Tab
                  value="tags"
                  leftSection={<IconHash size={14} />}
                  rightSection={tagResults.length > 0 ? <Badge size="xs" variant="light">{tagResults.length}</Badge> : undefined}
                >
                  Tags
                </Tabs.Tab>
              </Tabs.List>
            </Box>

            {/* Products */}
            <Tabs.Panel value="products">
              {isLoadingProducts && products.length === 0 ? (
                <Center py="xl"><Loader size="sm" /></Center>
              ) : products.length > 0 ? (
                <Stack gap="sm">
                  {products.map((p) => <ProductCard key={p.id} product={p} />)}
                  <div ref={productSentinel} />
                  {fetchingMoreProducts && <Center py="sm"><Loader size="sm" /></Center>}
                </Stack>
              ) : (
                <Center py={60}>
                  <Stack align="center" gap="xs">
                    <IconPackage size={36} color="var(--mantine-color-dimmed)" />
                    <Text c="dimmed">No products found for &ldquo;{submittedQuery}&rdquo;</Text>
                    <Text size="sm" c="dimmed">
                      Want to add it?{' '}
                      <Anchor component={Link} to="/reviews/create" size="sm">
                        Write a review
                      </Anchor>{' '}
                      and we'll create the product from your barcode scan.
                    </Text>
                  </Stack>
                </Center>
              )}
            </Tabs.Panel>

            {/* Reviews */}
            <Tabs.Panel value="reviews">
              {isLoadingReviews && reviews.length === 0 ? (
                <Center py="xl"><Loader size="sm" /></Center>
              ) : reviews.length > 0 ? (
                <Stack gap="sm">
                  {reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
                  <div ref={reviewSentinel} />
                  {fetchingMoreReviews && <Center py="sm"><Loader size="sm" /></Center>}
                </Stack>
              ) : (
                <Center py={60}>
                  <Text c="dimmed">No reviews found for &ldquo;{submittedQuery}&rdquo;</Text>
                </Center>
              )}
            </Tabs.Panel>

            {/* People */}
            <Tabs.Panel value="users">
              {isLoadingUsers && users.length === 0 ? (
                <Center py="xl"><Loader size="sm" /></Center>
              ) : users.length > 0 ? (
                <Stack gap="sm">
                  {users.map((u) => <UserCard key={u.id} user={u} />)}
                  <div ref={userSentinel} />
                  {fetchingMoreUsers && <Center py="sm"><Loader size="sm" /></Center>}
                </Stack>
              ) : (
                <Center py={60}>
                  <Text c="dimmed">No people found matching &ldquo;{submittedQuery}&rdquo;</Text>
                </Center>
              )}
            </Tabs.Panel>

            {/* Tags */}
            <Tabs.Panel value="tags">
              {isLoadingTags ? (
                <Center py="xl"><Loader size="sm" /></Center>
              ) : tagResults.length > 0 ? (
                <Group gap="sm" wrap="wrap">
                  {tagResults.map((tag) => (
                    <Badge
                      key={tag.id}
                      component={Link}
                      to={`/tags/${tag.slug}`}
                      variant="outline"
                      size="lg"
                      radius="xl"
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                      rightSection={
                        <Text span size="xs" c="dimmed" ml={4}>{tag.reviewCount}</Text>
                      }
                    >
                      #{tag.name}
                    </Badge>
                  ))}
                </Group>
              ) : (
                <Center py={60}>
                  <Text c="dimmed">No tags found matching &ldquo;{submittedQuery}&rdquo;</Text>
                </Center>
              )}
            </Tabs.Panel>
          </Tabs>
        )}

        {/* Empty state */}
        {!submittedQuery && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <IconSearch size={48} color="gray" />
              <Text c="dimmed" size="lg">Search for anything</Text>
              <Text c="dimmed" size="sm">Find products, reviews, people, and tags</Text>
            </Stack>
          </Center>
        )}
      </Stack>
    </Container>
  );
}

function ProductCard({ product }: { product: Product }) {
  return (
    <UnstyledButton component={Link} to={`/products/${product.id}`}>
      <Card shadow="xs" padding="md" radius="md" withBorder>
        <Group gap="md" wrap="nowrap">
          <Center
            w={56} h={56}
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))',
              flexShrink: 0, overflow: 'hidden',
            }}
          >
            {product.imageUrl
              ? <Image src={product.imageUrl} alt="" w={56} h={56} fit="contain" />
              : <IconPackage size={22} color="var(--mantine-color-dimmed)" />}
          </Center>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} lineClamp={1}>{product.name}</Text>
            {product.brand && <Text size="sm" c="dimmed">{product.brand}</Text>}
            <Group gap="xs" mt={2}>
              {product.averageRating != null && (
                <Rating value={product.averageRating} fractions={2} readOnly size="xs" />
              )}
              <Text size="xs" c="dimmed">{product.reviewCount} review{product.reviewCount !== 1 ? 's' : ''}</Text>
              {product.category && (
                <Badge size="xs" variant="light">{product.category}</Badge>
              )}
            </Group>
          </Stack>
        </Group>
      </Card>
    </UnstyledButton>
  );
}

function ReviewCard({ review }: { review: ReviewSearchResult }) {
  return (
    <UnstyledButton component={Link} to={`/reviews/${review.id}`}>
      <Card shadow="xs" padding="md" radius="md" withBorder>
        <Group gap="md" align="flex-start" wrap="nowrap">
          <Center
            w={56} h={56}
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))',
              flexShrink: 0, overflow: 'hidden',
            }}
          >
            {review.productImageUrl
              ? <Image src={review.productImageUrl} alt="" w={56} h={56} fit="contain" />
              : <IconPackage size={22} color="var(--mantine-color-dimmed)" />}
          </Center>

          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Text size="sm" fw={600} truncate c="dimmed">{review.productName}</Text>
              <StarRating rating={review.rating} size="xs" />
            </Group>
            <Text size="sm" lineClamp={2}>{review.contentSnippet}</Text>
            <Group gap="xs">
              <Avatar src={review.userAvatarUrl} size={18} radius="xl">
                <IconUser size={10} />
              </Avatar>
              <Text size="xs" c="dimmed">@{review.username}</Text>
              <Text size="xs" c="dimmed">·</Text>
              <Text size="xs" c="dimmed">{new Date(review.createdAt).toLocaleDateString()}</Text>
              {review.helpfulCount > 0 && (
                <>
                  <Text size="xs" c="dimmed">·</Text>
                  <Group gap={4}>
                    <IconThumbUp size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">{review.helpfulCount}</Text>
                  </Group>
                </>
              )}
              {review.commentCount > 0 && (
                <>
                  <Text size="xs" c="dimmed">·</Text>
                  <Group gap={4}>
                    <IconMessageCircle size={12} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">{review.commentCount}</Text>
                  </Group>
                </>
              )}
            </Group>
          </Stack>
        </Group>
      </Card>
    </UnstyledButton>
  );
}

function UserCard({ user }: { user: User }) {
  return (
    <UnstyledButton component={Link} to={`/users/${user.username}`}>
      <Card shadow="xs" padding="md" radius="md" withBorder>
        <Group gap="md">
          <Avatar src={user.profileImageUrl} alt={user.username} size="md" radius="xl">
            <IconUser size={20} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1 }}>
            <Text fw={500}>@{user.username}</Text>
            {user.bio && <Text size="sm" c="dimmed" lineClamp={1}>{user.bio}</Text>}
            {user.reviewCount != null && (
              <Text size="xs" c="dimmed">{user.reviewCount} review{user.reviewCount !== 1 ? 's' : ''}</Text>
            )}
          </Stack>
        </Group>
      </Card>
    </UnstyledButton>
  );
}
